import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box, Card, CardContent, Typography, TextField, Button,
    Switch, CircularProgress, Backdrop, Divider, IconButton
} from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader1 from '../../components/common/PageHeader1';
import { uploadImagecertifates } from '../../Redux/Actions/Action';
import { MdCloudUpload, MdDelete } from 'react-icons/md';

const AstroProfilePopup = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        subTitle: '',
        imageUrl: '',
        isActive: false
    });

    useEffect(() => {
        fetchProfilePopupData();
    }, []);

    const fetchProfilePopupData = async () => {
        setLoading(true);
        const token = localStorage.getItem("astroVani-admin-token");
        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/astroProfilePopup`, {
                headers: { 'Authorization': token }
            });
            const result = await response.json();
            if (response.ok && result?.data) {
                const { data } = result;
                setFormData({
                    title: data.title || '',
                    subTitle: data.subTitle || '',
                    imageUrl: data.imageUrl || '',
                    isActive: data.isActive || false
                });
            } else {
                toast.error(result.message || "Failed to fetch profile popup data");
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error("Something went wrong while fetching data");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleToggleChange = async (e) => {
        const newValue = e.target.checked;
        const token = localStorage.getItem("astroVani-admin-token");
        
        // Optimistic update
        setFormData(prev => ({ ...prev, isActive: newValue }));
        setActionLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/astroProfilePopup/toggle`, {
                method: 'PATCH',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ isActive: newValue })
            });
            const result = await response.json();
            if (response.ok) {
                toast.success(`Profile popup ${newValue ? 'activated' : 'deactivated'} successfully`);
            } else {
                toast.error(result.message || "Failed to toggle status");
                // Rollback on failure
                setFormData(prev => ({ ...prev, isActive: !newValue }));
            }
        } catch (error) {
            console.error("Toggle error:", error);
            toast.error("Something went wrong");
            setFormData(prev => ({ ...prev, isActive: !newValue }));
        } finally {
            setActionLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                const isString = typeof uploadedUrl === 'string';
                setFormData(prev => ({ ...prev, imageUrl: isString ? uploadedUrl : uploadedUrl[0] }));
                toast.success("Image uploaded successfully");
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Failed to upload image");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        const token = localStorage.getItem("astroVani-admin-token");

        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/astroProfilePopup`, {
                method: 'PATCH',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    title: formData.title,
                    subTitle: formData.subTitle,
                    imageUrl: formData.imageUrl
                })
            });
            const result = await response.json();
            if (response.ok) {
                toast.success("Profile popup updated successfully");
            } else {
                toast.error(result.message || "Failed to update profile popup");
            }
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Something went wrong during update");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <PageHeader1 
                    pagetitle="Info Card" 
                    routebutton={false}
                />

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Card sx={{ 
                        width: '100%', 
                        borderRadius: '16px', 
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{ 
                            p: 3, 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Configuration</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mr: 1, fontWeight: 600 }}>Active Status</Typography>
                                <Switch 
                                    checked={formData.isActive}
                                    onChange={handleToggleChange}
                                    color="primary"
                                />
                            </Box>
                        </Box>
                        <Divider />
                        <CardContent sx={{ p: 4 }}>
                            <form onSubmit={handleSubmit}>
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#555' }}>Popup Title</Typography>
                                    <TextField
                                        fullWidth
                                        name="title"
                                        placeholder="Enter title (e.g. Astrologer)"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                                        }}
                                    />
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#555' }}>Popup Subtitle</Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        name="subTitle"
                                        placeholder="Enter subtitle (e.g. Astrologer profile set up)"
                                        value={formData.subTitle}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                                        }}
                                    />
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#555' }}>Banner Icon</Typography>
                                    <Box sx={{ 
                                        border: '2px dashed #e0e0e0', 
                                        borderRadius: '16px', 
                                        p: 2, 
                                        textAlign: 'center',
                                        backgroundColor: '#fafafa',
                                        position: 'relative'
                                    }}>
                                        {formData.imageUrl ? (
                                            <Box sx={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '300px' }}>
                                                <img 
                                                    src={formData.imageUrl} 
                                                    alt="Preview" 
                                                    style={{ width: '100%', height: 'auto', borderRadius: '12px', display: 'block' }}
                                                />
                                                <IconButton 
                                                    onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                                                    sx={{ 
                                                        position: 'absolute', 
                                                        top: -10, 
                                                        right: -10, 
                                                        backgroundColor: '#f44336', 
                                                        color: '#fff',
                                                        '&:hover': { backgroundColor: '#d32f2f' }
                                                    }}
                                                    size="small"
                                                >
                                                    <MdDelete size={18} />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Box 
                                                onClick={() => fileInputRef.current.click()}
                                                sx={{ cursor: 'pointer', py: 4 }}
                                            >
                                                {uploading ? (
                                                    <CircularProgress size={32} />
                                                ) : (
                                                    <>
                                                        <MdCloudUpload size={48} color="#bdbdbd" />
                                                        <Typography sx={{ mt: 1, color: '#757575' }}>Click to upload banner image</Typography>
                                                    </>
                                                )}
                                            </Box>
                                        )}
                                        <input 
                                            type="file" 
                                            hidden 
                                            ref={fileInputRef} 
                                            onChange={handleFileUpload} 
                                            accept="image/*"
                                        />
                                    </Box>
                                </Box>

                                <Button 
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={actionLoading || uploading}
                                    sx={{ 
                                        py: 2, 
                                        borderRadius: '12px', 
                                        textTransform: 'none', 
                                        fontSize: '16px', 
                                        fontWeight: 700,
                                        backgroundColor: 'var(--primary-color, #E7B242)',
                                        '&:hover': { backgroundColor: '#d6a33b' }
                                    }}
                                >
                                    {actionLoading ? 'Updating...' : 'Update Profile Card'}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </Box>
            </div>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
};

export default AstroProfilePopup;
